import { ApolloClient, ApolloLink, createHttpLink } from '@apollo/client'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { buildClientSchema, IntrospectionQuery } from 'graphql'
import { withScalars } from 'apollo-link-scalars'
import { useMemo } from 'react'
import createInMemoryCache from './cache'
import graphQLSchema from './graphql.schema.json'
import { HicDateResolver } from './hic-date-resolver'
import { DateTimeResolver, TimeResolver } from 'graphql-scalars'
import { useAuth } from 'src/app/hooks/auth/auth.hook'
import { getMainDefinition } from '@apollo/client/utilities'

const useApolloClient = () => {
  const { auth } = useAuth()
  const client = useMemo(() => {
    const cache = createInMemoryCache()

    let linkChain = withScalars({
      schema: buildClientSchema(graphQLSchema as unknown as IntrospectionQuery),
      typesMap: {
        Date: HicDateResolver,
        DateTime: DateTimeResolver,
        Time: TimeResolver,
      },
    })

    linkChain = linkChain.concat(
      new ApolloLink((operation, forward) => {
        const context = operation.getContext()

        const headers: { [key: string]: string } = {}
        if (auth?.isAuthenticated) {
          headers.Authorization = `Bearer ${auth.token}`
        } else {
          headers.Authorization = `Bearer ${process.env.REACT_APP_PUBLIC_TOKEN}`
        }

        operation.setContext({ headers: { ...headers, ...context.headers } })

        return forward(operation)
      }),
    )

    if (process.env.REACT_APP_WS_URL) {
      linkChain = linkChain.split(
        ({ query }) => {
          // every operation of type "subscription" it's sent with WebSockets
          const definition = getMainDefinition(query)
          return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
        },
        new GraphQLWsLink(
          createClient({
            url: process.env.REACT_APP_WS_URL,
          }),
        ),
        createHttpLink({
          uri: process.env.REACT_APP_API_URL,
        }),
      )
    } else {
      linkChain = linkChain.concat(
        createHttpLink({
          uri: process.env.REACT_APP_API_URL,
        }),
      )
    }

    return new ApolloClient({
      cache: cache,
      link: linkChain,
      connectToDevTools: true,
      defaultOptions: {
        query: {
          fetchPolicy: 'network-only',
        },
        watchQuery: {
          fetchPolicy: 'network-only',
          nextFetchPolicy: 'cache-first',
        },
      },
    })
  }, [auth])

  return client
}

export default useApolloClient
